/* eslint-disable no-use-before-define */
import {
  join, call, fork, put, select,
} from 'redux-saga/effects';
import nsApi from '@netsapiens/netsapiens-js/dist/api';
import nsBrowser from '@netsapiens/netsapiens-js/dist/browser';
import nsPortal from '@netsapiens/netsapiens-js/dist/portal';
import nsToken from '@netsapiens/netsapiens-js/dist/token';
import nsUcLicense from '@netsapiens/netsapiens-js/dist/uc-license';

import * as actions from '../state';
import browserSupport, { BROWSER_CHECK_FAIL } from './services/browserSupport';
import checkAuthCode from './services/auth/checkAuthCode';
import fetchLogo from './services/fetchLogo';
import fetchLoginImage from './services/fetchLoginImage';
import fetchUser from './services/user/fetchUser';
import fetchBoardList from './services/board/fetchBoardList';
// eslint-disable-next-line import/no-cycle
import nsSocketConnected from './services/nsSocketConnected';

// setters & services
import { configureSdk } from './configureSdk';
import { loadAutocompleteChoices } from './loadAutocompleteChoices';
import { networkConnection } from './networkConnection';
// eslint-disable-next-line import/no-cycle
import { nsSocketConnect } from './nsSocketConnect';
import { setFavicon } from './setFavicon';
import { setTitle } from './setTitle';
import { userUiConfigs } from './userUiConfigs';
import { initializeReactGA } from './initializeReactGA';
import { fetchPremiumCreds } from './fetchPremiumCreds';

// events
import { changeBoard } from '../events';

// util
import { parseUrl } from '../utils';
import { initManifest } from '../charts-manifest';

// sdk error constants
const {
  API_TOKEN_REFRESH_FAIL,
  INVALID_TOKEN,
  NO_PORTAL_SESSION,
  NO_PORTAL_TOKEN,
  NO_STORED_TOKEN,
  UC_LICENSE_EXPIRED,
} = nsApi;

// these go across multiple sagas and need to be kept track of
let _boardListTask;
let _decodedToken;
let _isLoggedIn;
let _logoTask;
let _socketTask;
let _userTask;

const routeParams = parseUrl(window.location);

/**
 * Initial starting point for loading and configuring the app
 */
export function* preLogin() {
  try {
    yield fork(networkConnection);

    yield put(actions.showLoaderPage());

    // configure the sdk with the app name and portal/api url
    yield call(configureSdk);

    // fetch the login image
    // fetches from the api for custom branding
    const loginImage = yield call(fetchLoginImage);
    yield put(actions.setLoginImage(loginImage));

    // verify the uc licensing is valid
    const ucCheck = yield call(nsPortal.ucCheck);
    yield put(actions.setConfig({ apiHash: ucCheck.api_hash }));

    if (routeParams.reset) {
      yield call(resetInit);
    } else if (routeParams.public_id) {
      yield call(publicInit);
    } else {
      // if this line is reached the user has uc access
      // otherwise the catch will change the state to display the uc-license-msg component
      // get server ui configs and save to the core configs module
      yield call(userUiConfigs, nsToken.getDecoded());

      // test if the browser is supported // todo should we check browser support?
      const configBrowsers = yield select((state) => actions.selectBrowsers(state));
      yield call(browserSupport, configBrowsers);

      // if this line is reached the user is using a supported browser
      // otherwise the catch will change the state to display the browser-support-msg component
      // call sdk proxy authentication
      yield call(nsApi.proxyAuthentication, ucCheck.api_hash);

      // set _isLoggedIn so that the ui configs for the user won't be fetched again
      _isLoggedIn = true;

      // if this line is reached, the user authenticated successfully
      // otherwise the catch will change the state to display the login-form component
      // call the next generator function to continue the load process
      yield call(postLogin);
    }
  } catch (err) {
    let notice;

    // error handler
    switch (err) {
      case BROWSER_CHECK_FAIL:
        yield put(actions.showBrowserSupportPage());
        break;
      case UC_LICENSE_EXPIRED:
        // fetch the uc license notice
        // ui configurable
        notice = yield nsUcLicense.ucLicenseNotice();
        // change the application state to display the uc-license-msg component
        yield put(actions.showUCLicenseMsgPage(notice));
        break;
      case API_TOKEN_REFRESH_FAIL:
      case INVALID_TOKEN:
      case NO_PORTAL_SESSION:
      case NO_PORTAL_TOKEN:
      case NO_STORED_TOKEN:
        // remove the user query field from the url
        nsBrowser.removeQuery('user');
        // change the application state to display the login-form component
        yield put(actions.showLoginFormPage());
        break;
      default:
        // todo make generic error component
        console.error('Initialization Error: ', err);
        yield put(actions.showLoginFormPage());
        // yield put(actions.showUCLicenseMsgPage(err));
    }
  }
}

export function* resetInit() {
  // fetch and set generic UI configs, these contain password rules
  yield call(userUiConfigs);

  const username = routeParams.login;
  const authCode = routeParams.authcode;

  const isAuthCodeValid = yield call(checkAuthCode, { username, authCode });

  if (isAuthCodeValid) {
    // show password reset page
    _decodedToken = nsToken.getDecoded();
    yield call(userUiConfigs, _decodedToken);
    yield put(actions.showPasswordResetPage());
  } else {
    const appURL = yield select((state) => actions.selectConfig(state, 'appURL'));
    window.history.pushState({ urlPath: appURL }, '', appURL);

    yield put(actions.loginFormErrorMsg('Link has expired.'));
    yield put(actions.showLoginFormPage());
  }
}

export function* publicInit() {
  // fetch and set the favicon
  // fetches from the api for custom branding
  yield fork(setFavicon);

  // set browser tab title
  yield fork(setTitle);

  // fetch and set generic UI configs
  yield call(userUiConfigs);

  // fork fetching the logo
  // fetches from the api for custom branding
  // save a ref to the task for joining later in the load process
  const logo = yield call(fetchLogo);
  yield put(actions.setLogo(logo));

  yield put(actions.setChartsManifest(initManifest()));
  yield put(changeBoard(routeParams.public_id, true));
  yield put(actions.showWrapperPagePublic(true));
}

/**
 * Continuation of the load process after the user is authenticated
 */
export function* postLogin() {
  try {
    _decodedToken = nsToken.getDecoded();

    if (_decodedToken.user_scope === 'Call Center Agent') {
      const agentAccessEnabled = yield select((state) => actions.selectConfig(state, 'ANALYTICS_ENABLE_AGENTS_ACCESS'));

      if (!agentAccessEnabled) {
        // remove the user query field from the url
        nsBrowser.removeQuery('user');
        // change the application state to display the login-form component
        yield put(actions.loginFormErrorMsg('Agent access is not enabled.'));
        yield put(actions.showLoginFormPage());

        return false;
      }
    }

    // set a keepalive check
    setInterval(() => {
      // eslint-disable-next-line no-unused-expressions
      localStorage.getItem('ns_t') || window.location.reload();
    }, 2500);

    // set the userId for the app
    yield put(actions.setConfig({ userId: _decodedToken.user }));

    // set combined userId, this is necessary to make guest unique
    const combinedId = `${_decodedToken.user}`;
    yield put(actions.setConfig({ combinedId }));

    // determine if ui configs should be fetched again
    if (!_isLoggedIn && _decodedToken.user_scope !== 'guest') {
      yield call(userUiConfigs, _decodedToken);
    }

    const serviceEnabled = yield select((state) => actions.selectConfig(state, 'PORTAL_ANALYTICS_FEATURE_ENABLED'));
    if (!serviceEnabled || serviceEnabled !== 'yes') {
      yield put(actions.showNoServicePage(true));
      return false;
    }

    // fetch the user
    _userTask = yield fork(fetchUser, { ..._decodedToken, userId: combinedId });

    // save a ref to the task for joining later in the load process
    // _socketTask = yield fork(nsSocketConnect);
    yield fork(nsSocketConnect);
    _socketTask = yield fork(nsSocketConnected);

    // fetch the board list
    _boardListTask = yield fork(fetchBoardList, { ..._decodedToken, userId: combinedId });

    // fetch and set the favicon
    // fetches from the api for custom branding
    yield fork(setFavicon);

    // set browser tab title
    yield fork(setTitle);

    // fork fetching the logo
    // fetches from the api for custom branding
    // save a ref to the task for joining later in the load process
    _logoTask = yield fork(fetchLogo);

    // initialize google analytics
    yield fork(initializeReactGA);

    // if this line is reached, the init sequence is valid
    // otherwise the catch will change the state to display the *? component
    // call the next generator function to continue the load process
    yield call(setInitData);

    // const configBrowsers = yield select(state => actions.selectBrowsers(state));
  } catch (err) {
    console.error(err);
  }

  return true;
}

/**
 * Continuation of the login process if the user needs to select a subscriber to login as
 */
export function* subscriberSelect(event) {
  try {
    // eslint-disable-next-line camelcase
    const { subscriber_list } = event.payload;
    yield put(actions.showSubscriberSelectPage(subscriber_list));
  } catch (err) {
    console.error('subscriberSelect', err);
  }

  return true;
}

/**
 * Continuation of the login process if the user needs to enroll their subscriber to sso
 */
export function* ssoEnroll(event) {
  try {
    const { idtoken, vendor } = event.payload;

    yield put(actions.showSsoEnrollPage(idtoken, vendor));
  } catch (err) {
    console.error('ssoEnroll', err);
  }

  return true;
}

/**
 * Continuation of the load process.
 */
export function* setInitData() {
  yield put(actions.showLoaderPage());

  // pause the process until the async tasks are complete
  yield join([
    _boardListTask,
    _logoTask,
    _socketTask,
    _userTask,
  ]);

  const logo = _logoTask.result();
  yield put(actions.setLogo(logo));

  const user = _userTask.result();
  yield put(actions.setUser(user));

  // get the logo for the user's domain, if it is available
  const userLogo = yield call(fetchLogo, { domain: user.domain });
  if (userLogo) yield put(actions.setLogo(userLogo));

  const chartsManifest = initManifest(user.language); // optional messages for second param
  yield put(actions.setChartsManifest(chartsManifest));

  const boardList = _boardListTask.result();
  yield put(actions.setBoardList(boardList));

  if (routeParams.boardId) { // if a specific board is requested, route to it
    yield put(changeBoard(routeParams.boardId));
  } else if (boardList && boardList.length) { // if no board is selected but user has boards
    const firstFavorite = boardList.find((board) => board.favorite); // find the first favorited board
    const defaultBoard = firstFavorite ? firstFavorite.id : boardList[0].id; // default is either the first favorite or first board
    yield put(changeBoard(defaultBoard));
  }

  yield call(fetchPremiumCreds);

  // show the grid
  yield put(actions.showWrapperPage(true));

  // retrieve autocomplete choices last
  yield call(loadAutocompleteChoices, _decodedToken, ['scope', 'department', 'site', 'callqueue', 'subscriber']);
}
